// FILE: src/app/(portal)/claim/page.tsx
// VERSION: 2.0.0
// START_MODULE_CONTRACT:
// PURPOSE: [Публичная страница Claim Profile — заявка на подтверждение профиля ЧОП]
// SCOPE: [claim, seo_portal, claim_requests, trust_score]
// AI_REFLECTION: [SCORE: Q=8, R=8, M=9] [DEPTH: standard]
//   [NOTE: Клиентский компонент. Форма использует общую Zod схему из src/lib/validations/claim.ts.
//    Client-side + server-side валидация через Shared Zod Schema. Honeypot для антиспама.
//    Phase 8.5: добавлена Zod-валидация с детальными сообщениями об ошибках.]
// KEYWORDS: [DOMAIN(claim): claim-form, zod-validation, server-side]
// LINKS: [USES_API(zod): Zod shared schema; USES_API(/api/seo/claim-profile): API route]
// END_MODULE_CONTRACT

"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  Building2,
  Check,
  Loader2,
  LogIn,
  Mail,
  Phone,
  Shield,
  TrendingUp,
} from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { usePortalSession } from "@/features/seo-portal/components/portal-session-provider";
import { claimFormSchema } from "@/lib/validations/claim";

const BENEFITS = [
  "Повышение Индекса доверия на 15–35 баллов",
  "Отображение логотипа и описания компании",
  "Возможность отвечать на отзывы клиентов",
  "Приоритет в результатах рейтинга",
  "Уведомления о новых отзывах",
];

export default function ClaimProfilePage() {
  const { user, session, isLoading: authLoading } = usePortalSession();
  const [email, setEmail] = useState("");

  // Предзаполнить email из сессии при загрузке пользователя
  useEffect(() => {
    if (user?.email && !email) {
      setEmail(user.email);
    }
  }, [user?.email, email]);
  const [inn, setInn] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    // Honeypot антиспам
    if (honeypot) {
      setSuccess(true);
      return;
    }

    // Client-side Zod validation (shared schema)
    const validationResult = claimFormSchema.safeParse({
      companyName,
      inn: inn || undefined,
      email,
      phone: phone || undefined,
      comment: comment || undefined,
      consent,
    });

    if (!validationResult.success) {
      const errors: Record<string, string> = {};
      for (const issue of validationResult.error.issues) {
        const field = issue.path[0] as string;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/seo/claim-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          inn: inn || null,
          company_name: companyName,
          phone: phone || null,
          comment: comment || null,
          user_id: user?.id || null,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Ошибка сервера");
      }

      // 152-ФЗ: логируем согласие на обработку ПДн
      fetch("/api/pdpn/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "claim_profile",
          metadata: { email, company_name: companyName, inn: inn || null },
        }),
      }).catch(() => { /* silent */ });

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Произошла ошибка. Попробуйте позже или свяжитесь с нами: info@oxpa.pro");
    } finally {
      setLoading(false);
    }
  }

  // Helper: показать ошибку поля
  const fieldError = (field: string) => {
    return fieldErrors[field] ? (
      <p className="text-[10px] text-[hsl(var(--portal-danger))] mt-1 font-sans">{fieldErrors[field]}</p>
    ) : null;
  };

  if (success) {
    return (
      <div className="flex-1">
        <nav className="mb-8 px-6 container mx-auto">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
            <Link href="/" className="hover:text-[hsl(var(--portal-text))] transition-colors">
              Главная
            </Link>
            <span className="text-[hsl(var(--portal-text-dim))]">/</span>
            <span className="text-[hsl(var(--portal-text))]">Подтверждение профиля</span>
          </div>
        </nav>
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 border border-[hsl(var(--portal-accent))]/30 bg-[hsl(var(--portal-accent-muted))] flex items-center justify-center mx-auto mb-8">
              <Check className="w-8 h-8 text-[hsl(var(--portal-accent))]" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold uppercase tracking-tight mb-4">
              Заявка принята
            </h1>
            <p className="text-[hsl(var(--portal-text-secondary))] font-sans text-lg mb-8 leading-relaxed">
              Мы свяжемся с вами в течение <span className="font-mono tabular-nums">24</span> часов для верификации. После подтверждения
              ваш Индекс доверия будет пересчитан.
            </p>
            <Link href="/rating">
              <Button
                variant="outline"
                className="rounded-none border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface-2))] hover:text-[hsl(var(--portal-text))] h-12 px-8 uppercase tracking-widest text-xs font-bold"
              >
                Вернуться в рейтинг
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Если загрузка auth — показываем спиннер
  if (authLoading) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[hsl(var(--portal-text-muted))] animate-spin" />
      </div>
    );
  }

  // Если не авторизован — предлагаем войти
  if (!user) {
    return (
      <div className="flex-1">
        <nav className="mb-8 px-6 container mx-auto pt-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
            <Link href="/" className="hover:text-[hsl(var(--portal-text))] transition-colors">Главная</Link>
            <span className="text-[hsl(var(--portal-text-dim))]">/</span>
            <span className="text-[hsl(var(--portal-text))]">Подтверждение профиля</span>
          </div>
        </nav>
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))] flex items-center justify-center mx-auto mb-8">
              <LogIn className="w-8 h-8 text-[hsl(var(--portal-text-muted))]" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold uppercase tracking-tight mb-4">Войдите в аккаунт</h1>
            <p className="text-[hsl(var(--portal-text-secondary))] font-sans text-lg mb-8 leading-relaxed">
              Чтобы отправить заявку на подтверждение профиля, войдите или зарегистрируйтесь.
            </p>
            <Link href="/login?redirect=/claim">
              <Button className="bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] h-12 px-8 uppercase tracking-widest text-xs font-bold rounded-none shadow-[4px_4px_0_hsl(var(--portal-accent-hover))]">
                Войти / Зарегистрироваться
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1">
      {/* Breadcrumbs */}
      <nav className="mb-8 px-6 container mx-auto pt-8">
        <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
          <Link href="/" className="hover:text-[hsl(var(--portal-text))] transition-colors">
            Главная
          </Link>
          <span className="text-[hsl(var(--portal-text-dim))]">/</span>
          <span className="text-[hsl(var(--portal-text))]">Подтверждение профиля</span>
        </div>
      </nav>
      {/* Hero */}
      <div className="border-b border-[hsl(var(--portal-border))] py-16">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-3 mb-4 text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest">
            <Shield className="w-4 h-4 text-[hsl(var(--portal-accent))]" strokeWidth={1.5} />
            Подтверждение профиля
          </div>
          <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4">
            Подтвердите ваш ЧОП
          </h1>
          <p className="text-[hsl(var(--portal-text-secondary))] font-sans text-lg max-w-2xl leading-relaxed">
            Верифицируйте профиль вашей охранной организации и повысьте
            свой Индекс доверия. Это бесплатно и занимает не более <span className="font-mono tabular-nums">5</span> минут.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Форма */}
          <div>
            <h2 className="text-xl font-bold uppercase tracking-wide mb-8">
              Заявка на верификацию
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot (скрытое поле) */}
              <div
                className="opacity-0 absolute pointer-events-none"
                aria-hidden="true"
                tabIndex={-1}
              >
                <input
                  type="text"
                  name="website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div>
                <label className="block text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest mb-2">
                  Название компании *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--portal-text-dim))]" />
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="ООО ЧОП «Щит»"
                    className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] px-10 py-3 font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors"
                    required
                  />
                </div>
                {fieldError("companyName")}
              </div>

              <div>
                <label className="block text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest mb-2">
                  ИНН организации
                </label>
                <input
                  type="text"
                  value={inn}
                  onChange={(e) => setInn(e.target.value)}
                  placeholder="7707083893"
                  maxLength={12}
                  className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] px-4 py-3 font-mono tabular-nums text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors"
                />
                {fieldError("inn")}
              </div>

              <div>
                <label className="block text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest mb-2">
                  Эл. почта *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--portal-text-dim))]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="director@chop-shield.ru"
                    className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] px-10 py-3 font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors"
                    required
                  />
                </div>
                {fieldError("email")}
              </div>

              <div>
                <label className="block text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest mb-2">
                  Телефон
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--portal-text-dim))]" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+7 (999) 123-45-67"
                    className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] px-10 py-3 font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors"
                  />
                </div>
                {fieldError("phone")}
              </div>

              <div>
                <label className="block text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest mb-2">
                  Комментарий
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Дополнительная информация о вашей компании..."
                  rows={3}
                  className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] px-4 py-3 font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))] transition-colors resize-none"
                />
                {fieldError("comment")}
              </div>

              {error && (
                <div className="border border-[hsl(var(--portal-danger))]/30 bg-[hsl(var(--portal-danger))]/10 text-[hsl(var(--portal-danger))] text-sm font-sans px-4 py-3">
                  {error}
                </div>
              )}

              {/* 152-ФЗ: чекбокс явного согласия на обработку ПДн */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  disabled={loading}
                  className="mt-0.5 w-4 h-4 shrink-0 rounded-none border-[hsl(var(--portal-border-hover))] bg-[hsl(var(--portal-surface))] accent-[hsl(var(--portal-accent))]
                    focus:ring-0 focus:ring-offset-0
                    disabled:opacity-50 cursor-pointer"
                />
                <span className="text-[10px] text-[hsl(var(--portal-text-muted))] leading-relaxed group-hover:text-[hsl(var(--portal-text-secondary))] transition-colors font-sans">
                  Я даю согласие на обработку персональных данных в соответствии с{" "}
                  <a href="/privacy" target="_blank" className="underline text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text-secondary))] transition-colors">
                    Политикой конфиденциальности
                  </a>
                </span>
              </label>
              {fieldError("consent")}

              <Button
                type="submit"
                disabled={!consent || loading}
                className="w-full rounded-none h-14 bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] uppercase tracking-[0.15em] text-sm font-bold shadow-[4px_4px_0_hsl(var(--portal-accent-hover))] disabled:opacity-50 disabled:shadow-none"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Отправка...
                  </>
                ) : (
                  <>
                    Отправить заявку
                    <ArrowRight className="ml-3 w-4 h-4" strokeWidth={1.5} />
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Преимущества */}
          <div className="lg:pt-12">
            <div className="border border-[hsl(var(--portal-border))] p-8 md:p-10 mb-8">
              <div className="flex items-center gap-3 mb-6 text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest">
                <TrendingUp className="w-4 h-4 text-[hsl(var(--portal-accent))]" strokeWidth={1.5} />
                Зачем верифицировать профиль
              </div>
              <div className="space-y-5">
                {BENEFITS.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-4">
                    <div className="w-4 h-4 border border-[hsl(var(--portal-border-hover))] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 bg-[hsl(var(--portal-accent))]" />
                    </div>
                    <span className="text-sm text-[hsl(var(--portal-text-secondary))] font-sans leading-relaxed">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust Score Explainer */}
            <div className="border border-[hsl(var(--portal-border))] p-8 md:p-10">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-6 text-[hsl(var(--portal-text))]">
                Как работает Индекс доверия
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-[hsl(var(--portal-border))] pb-3">
                  <span className="text-xs text-[hsl(var(--portal-text-muted))] uppercase">Слой <span className="font-mono tabular-nums">1</span> — Основа</span>
                  <span className="text-xs text-[hsl(var(--portal-text-secondary))] font-bold font-mono tabular-nums">0–35 баллов</span>
                </div>
                <p className="text-[11px] text-[hsl(var(--portal-text-muted))] font-sans leading-relaxed">
                  Автоматический расчёт на основе статуса, возраста компании и полноты юридических данных.
                </p>
                <div className="flex justify-between items-center border-b border-[hsl(var(--portal-border))] pb-3 pt-2">
                  <span className="text-xs text-green-400 uppercase font-bold">Слой <span className="font-mono tabular-nums">2</span> — Верификация</span>
                  <span className="text-xs text-green-400 font-bold font-mono tabular-nums">0–35 баллов</span>
                </div>
                <p className="text-[11px] text-[hsl(var(--portal-text-muted))] font-sans leading-relaxed">
                  Подтверждение владения профилем, заполнение расширенных данных. Именно этот слой активируется
                  после подачи заявки.
                </p>
                <div className="flex justify-between items-center border-b border-[hsl(var(--portal-border))] pb-3 pt-2">
                  <span className="text-xs text-[hsl(var(--portal-text-muted))] uppercase">Слой <span className="font-mono tabular-nums">3</span> — Репутация</span>
                  <span className="text-xs text-[hsl(var(--portal-text-secondary))] font-bold font-mono tabular-nums">0–30 баллов</span>
                </div>
                <p className="text-[11px] text-[hsl(var(--portal-text-muted))] font-sans leading-relaxed">
                  Отзывы клиентов с байесовским усреднением для справедливой оценки.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}